import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Chip, LinearProgress, Typography, useMediaQuery } from '@mui/material';
import dayjs from 'dayjs';
import { THEME_COLOR } from '../../../theme/formStyles';
import { fmtRange, getProjectStatusStyle, stepProgressLabel, todayYmd } from '../projectSchedule.utils';

const STEP_LOOK = {
    done:     { ring: THEME_COLOR, fill: THEME_COLOR, line: THEME_COLOR, label: 'Done' },
    now:      { ring: THEME_COLOR, fill: '#fff', line: '#e5e9ee', label: 'In progress' },
    on_hold:  { ring: '#d4a017', fill: '#fff', line: '#e5e9ee', label: 'On hold' },
    upcoming: { ring: '#d5dbe1', fill: '#fff', line: '#e5e9ee', label: 'Upcoming' },
};

const handoverText = (handoverDate) => {
    if (!handoverDate) return '';
    const days = dayjs(handoverDate).diff(dayjs(todayYmd()), 'day');
    if (days > 0) return `🏁 Handover in ${days} day${days === 1 ? '' : 's'}`;
    if (days === 0) return '🏁 Handover today';
    return '🏁 Handover date passed';
};

function StepDot({ step }) {
    const look = STEP_LOOK[step.status] || STEP_LOOK.upcoming;
    const active = step.status === 'now' || step.status === 'on_hold';
    return (
        <Box sx={{
            width: 22, height: 22, borderRadius: '50%', flex: 'none', zIndex: 1,
            display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 700, color: '#fff',
            bgcolor: look.fill, border: `2px solid ${look.ring}`,
            boxShadow: active ? `0 0 0 4px ${step.status === 'now' ? '#e6f4f2' : '#fdf3d7'}` : 'none',
        }}>
            {step.status === 'done' ? '✓' : ''}
        </Box>
    );
}

// Saved steps use their own date text; task steps show their sub-task date range
export const stepDate = (step) => step.date_label || (step.start_date || step.end_date ? fmtRange(step.start_date, step.end_date) : 'Date TBD');
const stepKey = (step, i) => step.id ?? (step.task_id ? `t${step.task_id}` : `i${i}`);

function StepText({ step }) {
    const active = step.status === 'now' || step.status === 'on_hold';
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: active ? 700 : 500, lineHeight: 1.25, color: active ? THEME_COLOR : step.client_visible === false ? '#8a96a3' : '#1f2933' }}>
                {step.label}
                {step.client_visible === false && (
                    <Box component="span" sx={{ display: 'inline-block', ml: 0.5, fontSize: '10px', fontWeight: 700, color: '#8a96a3', bgcolor: '#eef1f4', borderRadius: 1, px: 0.6, verticalAlign: 'middle' }}>INTERNAL</Box>
                )}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#6b7785' }}>
                {stepDate(step)}
                {step.total_sub_tasks > 0 && ` · ${step.completed_sub_tasks}/${step.total_sub_tasks}`}
                {step.status === 'on_hold' && ' · On hold'}
            </Typography>
        </Box>
    );
}

// Summary line + one step per project task. Clicking a dated step jumps the calendar to its month.
export default function ProjectStepper({ project, onStepClick }) {
    const isPhone = useMediaQuery('(max-width:700px)');
    const [showAll, setShowAll] = useState(false);
    const scrollRef = useRef(null);
    const steps = project.steps || [];
    const { current, next, stepNo, total } = stepProgressLabel(project);
    const statusStyle = getProjectStatusStyle(project.status);
    const focusIdx = Math.max(0, steps.findIndex(s => s === (current || next)));

    useEffect(() => {
        setShowAll(false);
        // Bring the current step into view on wide screens
        const el = scrollRef.current?.querySelector('[data-focus="true"]');
        if (el && scrollRef.current) scrollRef.current.scrollLeft = Math.max(0, el.offsetLeft - 90);
    }, [project.id]);

    const summary = (
        <Box sx={{ border: '1px solid #e5e9ee', borderRadius: 3, p: '10px 14px', display: 'grid', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                {total > 0 && <Chip size="small" label={`Step ${stepNo} of ${total}`} sx={{ bgcolor: '#e6f4f2', color: THEME_COLOR, fontWeight: 700 }} />}
                <Typography sx={{ fontSize: '13.5px', flex: 1, minWidth: 200 }}>
                    {total === 0 ? 'No tasks added to this project yet'
                        : current ? <><b style={{ fontWeight: 500 }}>{current.label}</b> · {stepDate(current)}</>
                        : next ? <>Next: <b style={{ fontWeight: 500 }}>{next.label}</b> · {stepDate(next)}</>
                        : 'All steps done'}
                </Typography>
                <Typography sx={{ fontSize: '12.5px', color: '#6b7785' }}>{handoverText(project.handover_date)}</Typography>
                <Chip size="small" label={project.status} sx={{ fontWeight: 600, ...statusStyle }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinearProgress variant="determinate" value={project.progress} sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#e8eeec', '& .MuiLinearProgress-bar': { bgcolor: THEME_COLOR, borderRadius: 3 } }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: THEME_COLOR, minWidth: 110, textAlign: 'right' }}>
                    {project.progress}% · {project.completed_sub_tasks}/{project.total_sub_tasks} sub-tasks
                </Typography>
            </Box>
        </Box>
    );

    if (steps.length === 0) return summary;

    if (isPhone) {
        // Phones: vertical list with only the steps around the current one unless expanded
        const visible = steps.map((s, i) => ({ s, i })).filter(({ i }) => showAll || Math.abs(i - focusIdx) <= 1);
        const hidden = steps.length - visible.length;
        return (
            <Box>
                {summary}
                <Box sx={{ mt: 1 }}>
                    {visible.map(({ s, i }) => (
                        <Box key={stepKey(s, i)} onClick={() => onStepClick(s)}
                            sx={{ display: 'grid', gridTemplateColumns: '24px 1fr', columnGap: 1.5, alignItems: 'center', py: 1, px: 1, borderRadius: 2, cursor: 'pointer', bgcolor: i === focusIdx && current ? '#e6f4f2' : 'transparent' }}>
                            <StepDot step={s} />
                            <StepText step={s} />
                        </Box>
                    ))}
                    {(hidden > 0 || showAll) && (
                        <Button size="small" onClick={() => setShowAll(v => !v)} sx={{ color: THEME_COLOR, textTransform: 'none', fontWeight: 500 }}>
                            {showAll ? 'Show fewer steps ▲' : `Show all ${steps.length} steps ▼`}
                        </Button>
                    )}
                </Box>
            </Box>
        );
    }

    return (
        <Box>
            {summary}
            <Box ref={scrollRef} sx={{ display: 'flex', overflowX: 'auto', pt: 1.75, pb: 0.5, scrollbarWidth: 'thin' }}>
                {steps.map((s, i) => {
                    const look = STEP_LOOK[s.status] || STEP_LOOK.upcoming;
                    return (
                        <Box key={stepKey(s, i)} data-focus={i === focusIdx} onClick={() => onStepClick(s)}
                            sx={{ flex: '1 0 128px', pr: 1.25, cursor: s.start_date ? 'pointer' : 'default', '&:hover p:first-of-type': { color: THEME_COLOR } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <StepDot step={s} />
                                {i < steps.length - 1 && <Box sx={{ flex: 1, height: 4, borderRadius: 4, ml: '-2px', bgcolor: look.line }} />}
                            </Box>
                            <StepText step={s} />
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
